import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Repeat, TrendingUp, TrendingDown } from "lucide-react";

const CATS = [
  "loyer_recu", "loyer_verse", "solaire_revente", "revenus_autres",
  "charges_energie", "charges_eau", "charges_assurance",
  "charges_generales", "charges_impots", "charges_taxes",
  "solaire_charge", "frais_bancaires",
];

const empty = { entity_id: "", kind: "revenu", label: "", monthly_amount: 0,
                category: "loyer_recu", active: true, payment_day: "" };

export default function RecurringAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/backoffice/recurring").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
    ]).then(([i, e]) => { setItems(i); setEntities(e); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/backoffice/recurring", {
        ...form,
        monthly_amount: parseFloat(form.monthly_amount) || 0,
        payment_day: form.payment_day === "" ? null : (parseInt(form.payment_day) || null),
      });
      toast.success("Créé");
      setForm(empty); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const toggleCollected = async (i) => {
    const currentYm = new Date().toISOString().slice(0, 7);
    const isCollected = !!i.current_month_collected && i.current_month_collected_at === currentYm;
    try {
      await api.put(`/backoffice/recurring/${i.id}`, {
        current_month_collected: !isCollected,
        current_month_collected_at: !isCollected ? currentYm : null,
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const toggle = async (id, active) => {
    await api.put(`/backoffice/recurring/${id}`, { active: !active });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/backoffice/recurring/${id}`);
    load();
  };

  const totalR = items.filter((i) => i.kind === "revenu" && i.active).reduce((s, i) => s + (i.monthly_amount || 0), 0);
  const totalC = items.filter((i) => i.kind === "charge" && i.active).reduce((s, i) => s + (i.monthly_amount || 0), 0);

  return (
    <BackofficeLayout
      title="Revenus & Charges récurrents"
      subtitle="Sert de base au calcul du cash-flow mensuel prévisionnel. Loyers baux, charges fixes, etc."
      actions={<Btn onClick={() => { setForm({ ...empty, entity_id: entities[0]?.id || "" }); setShowForm(true); }} data-testid="bof-rec-new-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</Btn>}
    >
      <Toaster theme="dark" position="top-right" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Revenus / mois</div><div className="text-2xl font-black text-emerald-300 mt-1">{fmtEUR(totalR)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Charges / mois</div><div className="text-2xl font-black text-red-300 mt-1">{fmtEUR(totalC)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solde net / mois</div><div className={`text-2xl font-black mt-1 ${totalR - totalC >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtEUR(totalR - totalC)}</div></Card>
      </div>

      {showForm && (
        <Card className="mb-6" data-testid="bof-rec-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select required value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input">
              <option value="">Entité…</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input">
              <option value="revenu">Revenu</option>
              <option value="charge">Charge</option>
            </select>
            <input required placeholder="Libellé (ex: Loyer bail Cercottes)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input" />
            <input type="number" step="0.01" required placeholder="Montant / mois" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} className="input" data-testid="bof-rec-amount" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min="1" max="31" placeholder="Jour du mois (ex: 5)"
                   value={form.payment_day}
                   onChange={(e) => setForm({ ...form, payment_day: e.target.value })}
                   className="input" data-testid="bof-rec-payment-day" />
            <div className="md:col-span-3 flex gap-2">
              <Btn type="submit" data-testid="bof-rec-save">Créer</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowForm(false)}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card data-testid="bof-rec-list">
        {items.length === 0 ? (
          <div className="text-center text-slate-500 py-8">Aucun élément récurrent.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Libellé</th>
                <th className="py-2 pr-4">Entité</th>
                <th className="py-2 pr-4">Catégorie</th>
                <th className="py-2 pr-4 text-right">Montant / mois</th>
                <th className="py-2 pr-4 text-center">Jour</th>
                <th className="py-2 pr-4 text-center">Ce mois</th>
                <th className="py-2 pr-4">Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const ent = entities.find((e) => e.id === i.entity_id);
                const currentYm = new Date().toISOString().slice(0, 7);
                const isCollected = !!i.current_month_collected && i.current_month_collected_at === currentYm;
                return (
                  <tr key={i.id} className="border-b border-slate-800/50" data-testid={`bof-rec-${i.id}`}>
                    <td className="py-2 pr-4">
                      {i.kind === "revenu" ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-300"><TrendingUp className="w-3.5 h-3.5" /> Revenu</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-red-300"><TrendingDown className="w-3.5 h-3.5" /> Charge</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-bold">{i.label}</td>
                    <td className="py-2 pr-4 text-xs text-slate-400">{ent?.name}</td>
                    <td className="py-2 pr-4 text-xs">{i.category}</td>
                    <td className="py-2 pr-4 text-right font-mono">{fmtEUR(i.monthly_amount)}</td>
                    <td className="py-2 pr-4 text-center text-xs text-slate-400">{i.payment_day || "—"}</td>
                    <td className="py-2 pr-4 text-center">
                      <button onClick={() => toggleCollected(i)}
                              className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${isCollected
                                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                : "bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-700"}`}
                              data-testid={`bof-rec-toggle-collected-${i.id}`}>
                        {isCollected ? "✓ Passé" : "À venir"}
                      </button>
                    </td>
                    <td className="py-2 pr-4">
                      <button onClick={() => toggle(i.id, i.active)} className={`text-xs px-2 py-0.5 rounded border ${i.active ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-slate-800 border-slate-700 text-slate-500"}`}>
                        {i.active ? "Actif" : "Inactif"}
                      </button>
                    </td>
                    <td className="py-2 pr-2">
                      <button onClick={() => remove(i.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}
